import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang, UI } from "@/i18n";
import { useContent } from "@/content";
import { Monogram } from "@/components/Monogram";
import SocialLinks from "@/components/Social";

const anchors = [
  { id: "experience", key: UI.nav.experience },
  { id: "servizi", key: UI.nav.services },
  { id: "viaggiare", key: UI.nav.vehicle },
  { id: "contatti", key: UI.nav.contacts },
];

const waHref = (contact, lang) =>
  `https://wa.me/${((contact && contact.whatsapp) || "393751163111").toString().replace(/\D/g, "")}?text=${encodeURIComponent(
    contact && contact.whatsapp_msg
      ? contact.whatsapp_msg[lang] || contact.whatsapp_msg.it
      : "Ciao, vorrei prenotare un transfer con Obsidian Private Driver."
  )}`;

export default function Nav() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { content } = useContent();
  const contact = (content && content.contacts) || { whatsapp: "393751163111", whatsapp_msg: null };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  if (location.pathname.startsWith("/admin")) return null;

  const go = (id) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-[#0A0A0C]/85 backdrop-blur-md border-b hairline" : "bg-transparent"
        }`}
        data-testid="main-nav"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10 lg:px-16">
          <Link to="/" className="flex items-center" data-testid="nav-logo-link" aria-label="OBSIDIAN Private Driver">
            <img src="/media/logo_white.png" alt="OBSIDIAN Private Driver" className="h-11 w-auto" data-testid="nav-logo-img" />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Main">
            {anchors.map((a) => (
              <a
                key={a.id}
                href={`#${a.id}`}
                onClick={go(a.id)}
                className="text-[10px] uppercase tracking-[0.3em] text-[#FAF9F6]/70 transition-colors hover:text-[#C5A059]"
                data-testid={`nav-anchor-${a.id}`}
              >
                {t(a.key)}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4 pl-1">
            <div className="flex items-center gap-2 text-[12px] tracking-[0.2em] lg:ml-2" data-testid="lang-switch">
              {["it", "en"].map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`uppercase transition-colors ${lang === l ? "text-[#C5A059]" : "text-[#FAF9F6]/50 hover:text-[#FAF9F6]"}`}
                  data-testid={`lang-${l}`}
                  aria-pressed={lang === l}
                >
                  {l}
                </button>
              ))}
            </div>
            <SocialLinks size={16} className="hidden md:flex" />
            {contact.whatsapp && (
              <a
                href={waHref(contact, lang)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold !hidden md:!inline-flex !px-6 !py-2.5"
                data-testid="nav-cta-whatsapp"
              >
                {t(UI.nav.cta)}
              </a>
            )}
            <button
              className="flex h-9 w-9 flex-col items-center justify-center gap-[5px] lg:hidden"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
              data-testid="nav-mobile-toggle"
            >
              <span className={`h-px w-6 bg-[#FAF9F6] transition-transform duration-300 ${menuOpen ? "translate-y-[3px] rotate-45" : ""}`} />
              <span className={`h-px w-6 bg-[#FAF9F6] transition-transform duration-300 ${menuOpen ? "-translate-y-[3px] -rotate-45" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-8 bg-[#0A0A0C]/98 backdrop-blur-lg lg:hidden"
            data-testid="mobile-menu"
          >
            {anchors.map((a, i) => (
              <motion.a
                key={a.id}
                href={`#${a.id}`}
                onClick={go(a.id)}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.5 }}
                className="font-display text-3xl font-light text-[#FAF9F6]"
              >
                {t(a.key)}
              </motion.a>
            ))}
            {contact.whatsapp && (
              <a href={waHref(contact, lang)} target="_blank" rel="noopener noreferrer" className="btn-gold mt-4" data-testid="mobile-menu-cta">
                {t(UI.nav.cta)}
              </a>
            )}
            <SocialLinks className="mt-6" />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export { waHref };
